import { restoreKmlSource } from './kml-source.js';
import { hasCompleteStrokePoints } from './stroke-points.js';
import { applyMarkerDetails } from './marker-details.js';
import { applyDrawingLegend } from './drawing-legend.js';
import { EXPORT_MAP_ID_PATTERN } from './export-evidence.js';
import { applyTravelTimeArea } from './travel-time-area.js';
import { applyDrawingGeographicSource } from './drawing-geographic-source.js';
import { parseImportLinkState, type ParsedImports } from './import-link-state.js';
import { normalizePolygonOpacity } from './stroke-opacity.js';
import { isNumber, isSchemaRecord, isString, ownValue } from './runtime-values.js';
import { object, optional, unknown as unknownSchema } from 'zod/mini';
import type { SchemaInput } from './schema-input.js';
import { decodeStrokePoints } from './url-codec.js';
import { normalizeServiceAreaConfig, type ServiceAreaConfig } from './service-area-config.js';
import type { MapImage, Point, Stroke } from '../types/app.js';
import { isMarkerIconName } from './marker-icon.js';
import { MAP_IMAGE_ASSET_ID_PATTERN, MAX_MAP_IMAGES, isMapImageDataUrl } from './map-image.js';
import { maxStrokeWidth } from './stroke-limits.js';
import { isLineOrArrowTool } from './stroke-tool.js';
import { savedMapEditorSchema, type SavedMapEditor } from './saved-map-editor.js';
import { savedMeasurementSchema } from './saved-measurement.js';
import { parseServiceAreaBuilderDraft } from './service-area-draft.js';
import {
  DEFAULT_DRAWING_LAYER_ID,
  drawingLayerId,
  normalizeDrawingLayers,
  type DrawingLayer,
} from './drawing-layer.js';

type CompactTool = 'f' | 'l' | 'a' | 'c' | 'p' | 'm' | 'x';
type ParsedTool = 'freehand' | 'line' | 'arrow' | 'circle' | 'polygon' | 'marker' | 'text';

export interface ParsedSharedMapStroke extends Partial<Stroke> {
  points: Point[];
  color: string;
  width: number;
  tool: ParsedTool;
  zoom: number | undefined;
  zoomScale: boolean;
}

export interface GlobeCameraState { pitch: number; bearing: number; terrain?: boolean }

export interface ParsedSharedMapState {
  editor?: SavedMapEditor;
  rotation?: number;
  title?: string;
  showLayerLegend?: boolean;
  exportMapIdentity?: string;
  globeCamera: GlobeCameraState | null;
  imports?: ParsedImports;
  center: Point | null;
  zoom: number | null;
  basemap: string | null;
  strokes: ParsedSharedMapStroke[];
  images: Array<Omit<MapImage, 'id'> & { id?: string }>;
  hasStrokeList: boolean;
  serviceAreaConfig: ServiceAreaConfig | null;
  drawingLayers: DrawingLayer[];
  activeDrawingLayerId: string;
}

const TOOL_BY_COMPACT = {
  f: 'freehand',
  l: 'line',
  a: 'arrow',
  c: 'circle',
  p: 'polygon',
  m: 'marker',
  x: 'text',
} satisfies Record<CompactTool, ParsedTool>;

const DEFAULT_STROKE_COLOR = '#5b4bff';
const DEFAULT_STROKE_WIDTH = 4;
export const MAX_SHARED_MAP_STROKES = 1_000_000;
export const MAX_SHARED_MAP_POINTS_PER_STROKE = 50_000;
export const MAX_SHARED_MAP_TOTAL_POINTS = 100_000;

const compactSharedMapStateSchema = object({
  editor: optional(unknownSchema()),
  measurement: optional(unknownSchema()),
  serviceAreaDraft: optional(unknownSchema()),
  r: optional(unknownSchema()),
  n: optional(unknownSchema()),
  ei: optional(unknownSchema()),
  c: optional(unknownSchema()),
  z: optional(unknownSchema()),
  b: optional(unknownSchema()),
  s: optional(unknownSchema()),
  i: optional(unknownSchema()),
  sa: optional(unknownSchema()),
  dl: optional(unknownSchema()),
  da: optional(unknownSchema()),
  ll: optional(unknownSchema()),
  im: optional(unknownSchema()),
  v: optional(unknownSchema()),
});

const compactMapImageSchema = object({
  d: optional(unknownSchema()),
  group: optional(unknownSchema()),
  locked: optional(unknownSchema()),
  id: optional(unknownSchema()),
  h: optional(unknownSchema()),
  n: optional(unknownSchema()),
  a: optional(unknownSchema()),
  p: optional(unknownSchema()),
  o: optional(unknownSchema()),
});

const compactStrokeSchema = object({
  ks: optional(unknownSchema()),
  gs: optional(unknownSchema()),
  lg: optional(unknownSchema()),
  id: optional(unknownSchema()),
  p: optional(unknownSchema()),
  c: optional(unknownSchema()),
  w: optional(unknownSchema()),
  t: optional(unknownSchema()),
  z: optional(unknownSchema()),
  zs: optional(unknownSchema()),
  o: optional(unknownSchema()),
  fc: optional(unknownSchema()),
  fo: optional(unknownSchema()),
  tt: optional(unknownSchema()),
  oo: optional(unknownSchema()),
  ls: optional(unknownSchema()),
  q: optional(unknownSchema()),
  g: optional(unknownSchema()),
  tx: optional(unknownSchema()),
  r: optional(unknownSchema()),
  mi: optional(unknownSchema()),
  ma: optional(unknownSchema()),
  mn: optional(unknownSchema()),
  lo: optional(unknownSchema()),
  pr: optional(unknownSchema()),
  l: optional(unknownSchema()),
});

function finiteNumber(value: SchemaInput): number | null {
  return isNumber(value) && Number.isFinite(value) ? value : null;
}

function parseGlobeCamera(value: SchemaInput): GlobeCameraState | null {
  if (!isSchemaRecord(value)) return null;
  const pitch = finiteNumber(value.p);
  const bearing = finiteNumber(value.b);
  if (pitch === null || bearing === null) return null;
  const camera: GlobeCameraState = { pitch: Math.max(0, Math.min(60, pitch)), bearing: ((bearing % 360 + 540) % 360) - 180 };
  if (value.t === true || value.t === false) camera.terrain = value.t;
  return camera;
}

function parseZoom(value: SchemaInput): number | null {
  const zoom = finiteNumber(value);
  if (zoom === null) return null;
  return Math.max(0, Math.min(24, zoom));
}

function boundedStrokeText(value: string, tool: ParsedTool): string {
  return value.slice(0, tool === 'marker' ? 120 : 500);
}

function parseScaledCenter(value: SchemaInput): Point | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const lat = finiteNumber(value[0]);
  const lng = finiteNumber(value[1]);
  if (lat === null || lng === null) return null;
  const center = { lat: lat / 1e4, lng: lng / 1e4 };
  return isFiniteLatLng(center) ? center : null;
}

function parseBasemap(value: SchemaInput): string | null {
  return isString(value) && value.trim() ? value : null;
}

function parseColor(value: SchemaInput): string {
  if (!isString(value)) return DEFAULT_STROKE_COLOR;
  const compact = value.startsWith('#') ? value.slice(1) : value;
  return /^[0-9a-fA-F]{6}$/.test(compact) ? `#${compact.toLowerCase()}` : DEFAULT_STROKE_COLOR;
}

function parseWidth(value: SchemaInput, tool: ParsedTool): number {
  const width = finiteNumber(value);
  if (width === null) return DEFAULT_STROKE_WIDTH;
  return Math.max(1, Math.min(maxStrokeWidth(tool), Math.round(width)));
}

function parseRotation(value: SchemaInput): number | undefined {
  const rotation = finiteNumber(value);
  return rotation === null ? undefined : rotation;
}

function parseOpacity(value: SchemaInput): number | undefined {
  const opacityPercent = finiteNumber(value);
  if (opacityPercent === null) return undefined;
  return Math.max(0, Math.min(100, opacityPercent)) / 100;
}

function parsePressures(value: SchemaInput, pointCount: number): number[] | undefined {
  if (!Array.isArray(value) || value.length !== pointCount) return undefined;
  const pressures = value.map(finiteNumber);
  if (pressures.some((pressure) => pressure === null)) return undefined;
  return pressures.map((pressure) => Math.max(0, Math.min(100, pressure!)) / 100);
}

function parseCurveControls(value: SchemaInput, pointCount: number): Array<Point | null> | undefined {
  if (!Array.isArray(value) || value.length > pointCount - 1) return undefined;
  const controls = Array.from({ length: pointCount - 1 }, (_, index) => {
    const encoded = value[index];
    if (encoded === null || encoded === undefined) return null;
    const points = parseEncodedPoints(encoded);
    return points?.length === 1 ? points[0] : null;
  });
  return controls.some(Boolean) ? controls : undefined;
}

function applyStrokeText(
  stroke: ParsedSharedMapStroke,
  tool: ParsedTool,
  textValue: SchemaInput,
  labelOffsetValue: SchemaInput,
): void {
  if ((tool !== 'text' && tool !== 'marker') || !isString(textValue) || textValue.length === 0) return;
  stroke.text = boundedStrokeText(textValue, tool);
  if (tool !== 'text') return;
  const markerLabelOffset = finiteNumber(labelOffsetValue);
  if (markerLabelOffset !== null) stroke.markerLabelOffset = Math.max(0, Math.min(200, markerLabelOffset));
}

function parseLineStyle(value: SchemaInput): 'dashed' | undefined {
  return value === 'd' ? 'dashed' : undefined;
}

function parseTool(value: SchemaInput): ParsedTool {
  return isString(value) ? ownValue(TOOL_BY_COMPACT, value) ?? 'freehand' : 'freehand';
}

function parseZoomScale(value: SchemaInput): boolean {
  return isNumber(value) ? value === 1 : true;
}

function isFiniteLatLng(point: Point): boolean {
  return Number.isFinite(point.lat)
    && Number.isFinite(point.lng)
    && Math.abs(point.lat) <= 90;
}

function parseEncodedPoints(value: SchemaInput): Point[] | null {
  if (!Array.isArray(value) || value.length < 2 || value.length % 2 !== 0
    || value.length > MAX_SHARED_MAP_POINTS_PER_STROKE * 2) return null;
  if (!value.every((entry) => isNumber(entry) && Number.isFinite(entry))) return null;
  try {
    const points = decodeStrokePoints(value);
    return points.every(isFiniteLatLng) ? points : null;
  } catch {
    return null;
  }
}

function exceedsRawStrokeLimits(strokeList: SchemaInput[]): boolean {
  if (strokeList.length > MAX_SHARED_MAP_STROKES) return true;
  let rawPointCount = 0;
  for (const value of strokeList) {
    if (!isSchemaRecord(value) || !Array.isArray(value.p)) continue;
    if (value.p.length > MAX_SHARED_MAP_POINTS_PER_STROKE * 2) return true;
    rawPointCount += Math.floor(value.p.length / 2);
    if (rawPointCount > MAX_SHARED_MAP_TOTAL_POINTS) return true;
  }
  return false;
}

function parseStrokeList(strokeList: SchemaInput[]): ParsedSharedMapStroke[] | null {
  const strokes: ParsedSharedMapStroke[] = [];
  let totalPoints = 0;
  for (const value of strokeList) {
    const stroke = parseCompactStroke(value);
    if (!stroke) continue;
    totalPoints += stroke.points.length;
    if (totalPoints > MAX_SHARED_MAP_TOTAL_POINTS) return null;
    strokes.push(stroke);
  }
  return strokes;
}

function parseMapImageList(value: SchemaInput): Array<Omit<MapImage, 'id'> & { id?: string }> | null {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > MAX_MAP_IMAGES) return null;
  const images: Array<Omit<MapImage, 'id'> & { id?: string }> = [];
  for (const candidate of value) {
    const result = compactMapImageSchema.safeParse(candidate);
    if (!result.success) return null;
    const assetId = result.data.a;
    const points = parseEncodedPoints(result.data.p);
    if (!isString(assetId) || !MAP_IMAGE_ASSET_ID_PATTERN.test(assetId) || !points || points.length !== 4) return null;
    const opacity = finiteNumber(result.data.o);
    const image: Omit<MapImage, 'id'> & { id?: string } = {
      assetId,
      // SAFETY: The adjacent length check proves that all four tuple entries exist.
      points: [points[0], points[1], points[2], points[3]] as [Point, Point, Point, Point],
      opacity: opacity === null ? 1 : Math.max(10, Math.min(100, opacity)) / 100,
    };
    const id = parseDrawingId(result.data.id);
    if (id) image.id = id;
    if (Array.isArray(result.data.group)) image.group = result.data.group.filter(isString).map(name => name.slice(0, 80)).slice(0, 6);
    if (result.data.locked === true) image.locked = true;
    if (result.data.h === true) image.hidden = true;
    if (isString(result.data.n)) image.name = result.data.n.slice(0, 120);
    if (result.data.d !== undefined) {
      if (!isMapImageDataUrl(result.data.d)) return null;
      image.dataUrl = result.data.d;
    }
    images.push(image);
  }
  return images;
}

function parseMapContent(strokeList: SchemaInput[], imageList: SchemaInput): { strokes: ParsedSharedMapStroke[]; images: Array<Omit<MapImage, 'id'> & { id?: string }> } | null {
  if (exceedsRawStrokeLimits(strokeList)) return null;
  const strokes = parseStrokeList(strokeList);
  if (!strokes) return null;
  const images = parseMapImageList(imageList);
  return images ? { strokes, images } : null;
}

function activeDrawingLayer(drawingLayers: ParsedSharedMapState['drawingLayers'], requested: SchemaInput): string {
  const id = drawingLayerId(requested);
  return id && drawingLayers.some(layer => layer.id === id) ? id : DEFAULT_DRAWING_LAYER_ID;
}

function hasMapInstructions(center: ParsedSharedMapState['center'], zoom: ParsedSharedMapState['zoom'], basemap: ParsedSharedMapState['basemap'], serviceAreaConfig: ParsedSharedMapState['serviceAreaConfig'], drawingLayers: ParsedSharedMapState['drawingLayers']): boolean {
  return Boolean(center || zoom !== null || basemap || serviceAreaConfig || drawingLayers.length !== 1);
}

export function parseCompactSharedMapState(value: SchemaInput): ParsedSharedMapState | null {
  const result = compactSharedMapStateSchema.safeParse(value);
  if (!result.success) return null;
  const parsed = result.data;

  if (!validTypedMapContent(parsed)) return null;
  const editor = savedMapEditorSchema.safeParse(parsed.editor);

  const center = parseScaledCenter(parsed.c);
  const zoom = parseZoom(parsed.z);
  const basemap = parseBasemap(parsed.b);
  const serviceAreaConfig = normalizeServiceAreaConfig(parsed.sa);
  const drawingLayers = normalizeDrawingLayers(parsed.dl);
  const activeDrawingLayerId = activeDrawingLayer(drawingLayers, parsed.da);
  const strokeList = Array.isArray(parsed.s) ? parsed.s : null;
  const hasStrokeList = strokeList !== null;
  const rawStrokeCount = strokeList?.length ?? 0;
  const content = parseMapContent(strokeList ?? [], parsed.i);
  if (!content) return null;
  const { strokes, images } = content;

  if (!hasMapInstructions(center, zoom, basemap, serviceAreaConfig, drawingLayers)) {
    if (!hasStrokeList) return null;
    if (rawStrokeCount > 0 && strokes.length === 0) return null;
  }

  const state: ParsedSharedMapState = {
    showLayerLegend: parsed.ll === true,
    exportMapIdentity: isString(parsed.ei) && EXPORT_MAP_ID_PATTERN.test(parsed.ei) ? parsed.ei : undefined,
    globeCamera: parseGlobeCamera(parsed.v),
    center,
    zoom,
    basemap,
    strokes,
    images,
    hasStrokeList,
    serviceAreaConfig,
    drawingLayers,
    activeDrawingLayerId,
  };
  if (isNumber(parsed.r) && Number.isFinite(parsed.r)) state.rotation = parsed.r;
  if (editor?.success) state.editor = editor.data;
  if (isString(parsed.n)) state.title = parsed.n.slice(0, 500);
  if (parsed.im !== undefined) state.imports = parseImportLinkState(parsed.im);
  return state;
}

function validTypedMapContent(value: { editor?: SchemaInput; measurement?: SchemaInput; serviceAreaDraft?: SchemaInput }): boolean {
  if (value.editor !== undefined && !savedMapEditorSchema.safeParse(value.editor).success) return false;
  if (value.measurement !== undefined && !savedMeasurementSchema.safeParse(value.measurement).success) return false;
  return value.serviceAreaDraft === undefined || parseServiceAreaBuilderDraft(value.serviceAreaDraft) !== null;
}

function applyPolygonOutlineOpacity(stroke: ParsedSharedMapStroke, value: SchemaInput): void {
  const opacity = parseOpacity(value);
  if (opacity !== undefined) stroke.outlineOpacity = opacity;
}

function parseDrawingId(value: SchemaInput): string | undefined {
  return isString(value) && /^[A-Za-z0-9_-]{1,100}$/.test(value) ? value : undefined;
}

function parseCompactStroke(value: SchemaInput): ParsedSharedMapStroke | null {
  const result = compactStrokeSchema.safeParse(value);
  if (!result.success) return null;
  const parsed = result.data;
  const tool = parseTool(parsed.t);
  const points = parseEncodedPoints(parsed.p);
  if (!points || !hasCompleteStrokePoints(tool, points)) return null;

  const stroke: ParsedSharedMapStroke = {
    id: parseDrawingId(parsed.id),
    points,
    color: parseColor(parsed.c),
    width: parseWidth(parsed.w, tool),
    tool,
    zoom: parseZoom(parsed.z) ?? undefined,
    zoomScale: parseZoomScale(parsed.zs),
  };

  const opacity = parseOpacity(parsed.o);
  if (opacity !== undefined) stroke.opacity = opacity;
  restoreKmlSource(stroke, parsed.ks);
  applyTravelTimeArea(stroke, parsed.tt);
  applyDrawingGeographicSource(stroke, parsed.gs);
  if (tool === 'polygon') {
    stroke.fillColor = parsed.fc === undefined ? stroke.color : parseColor(parsed.fc);
    stroke.fillOpacity = parseOpacity(parsed.fo) ?? 0.25;
    applyPolygonOutlineOpacity(stroke, parsed.oo);
  }

  const lineStyle = parseLineStyle(parsed.ls);
  if (lineStyle) stroke.lineStyle = lineStyle;
  if (isLineOrArrowTool(tool)) {
    const curveControls = parseCurveControls(parsed.q, points.length);
    if (curveControls) stroke.curveControls = curveControls;
  }
  if (parsed.g === 0) stroke.geodesic = false;
  if (tool === 'freehand') {
    const pressures = parsePressures(parsed.pr, points.length);
    if (pressures) stroke.pressures = pressures;
  }

  applyStrokeText(stroke, tool, parsed.tx, parsed.lo);
  applyMarkerDetails(stroke, parsed.ma, parsed.mn);
  if (tool === 'marker' && isMarkerIconName(parsed.mi)) stroke.markerIcon = parsed.mi;

  const rotation = parseRotation(parsed.r);
  if (rotation !== undefined) stroke.rotation = rotation;

  const layerId = drawingLayerId(parsed.l);
  if (layerId && layerId !== DEFAULT_DRAWING_LAYER_ID) stroke.layerId = layerId;

  applyDrawingLegend(stroke, parsed.lg);
  normalizePolygonOpacity(stroke);
  return stroke;
}
